import { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import {
  isComlexExam,
  isCustomExam,
  isNbmeExam,
  isNclexExam,
  isPreclinicalExam,
} from '../../../utils/examUtils';
import {
  fetchFlashcardProgressStats,
  fetchHistoryStats,
  fetchProgressStats,
  fetchQbankPerformanceStats,
  fetchTopicPerformanceStats,
  fetchTopicTreeStats,
  fetchTopicTreeStatsWithReadiness,
  fetchVanityStats,
} from '../../../services/stats';
import type { TopicTreeView } from '../../../services/topics';
import {
  fetchFlashcardCounts,
  fetchWorkloadFlashcardCounts,
} from '../../../services/flashcards';
import { UserExam } from '../../../types/User';
import { useIsCourseStudent } from '../../../instances/useInstance';

/**
 * Optional override layer for the performance dashboard widgets.
 *
 * Every widget reads "whose data" through the current user's primary exam
 * (Redux), the v5 stats/flashcard services, and per-widget localStorage toggle
 * keys. A `PerformanceScope` lets a host (e.g. the faculty student-profile page)
 * substitute all three so the same widgets can render another student's data.
 *
 * When NO provider is present, the hooks below fall back to the exact current
 * behavior, so the user-facing `/performance` page (and every other render site)
 * is unaffected.
 */
export interface PerformanceScope {
  /** The subject's primary exam (drives layout + default exam-scoped fetches). */
  primaryExam: UserExam | undefined;
  /** Hide affordances that would act on the current user (nav, edit, delete). */
  readOnly: boolean;
  fetchVanity: typeof fetchVanityStats;
  fetchHistory: typeof fetchHistoryStats;
  fetchQbankPerformance: typeof fetchQbankPerformanceStats;
  fetchFlashcardProgress: typeof fetchFlashcardProgressStats;
  fetchProgress: typeof fetchProgressStats;
  fetchTopicTree: typeof fetchTopicTreeStats;
  /**
   * The tree plus the envelope's `readinessEnabled`. Optional: a host without
   * it never gets readiness (the faculty student detail supplies it, and its
   * backend route decides, ORA-1105).
   */
  fetchTopicTreeWithReadiness?: typeof fetchTopicTreeStatsWithReadiness;
  fetchTopicPerformance: typeof fetchTopicPerformanceStats;
  fetchFlashcardCounts: typeof fetchFlashcardCounts;
  fetchWorkloadFlashcardCounts: typeof fetchWorkloadFlashcardCounts;
  /** Toggle persistence, namespaced/ephemeral per host (never the user's prefs). */
  getToggle: (key: string) => string | null;
  setToggle: (key: string, value: string) => void;
}

const PerformanceScopeContext = createContext<PerformanceScope | null>(null);

export const PerformanceScopeProvider = PerformanceScopeContext.Provider;

export const usePerformanceScope = () => useContext(PerformanceScopeContext);

/** Scope-aware primary exam; falls back to the current user's primary exam. */
export const useScopedPrimaryExam = (): UserExam | undefined => {
  const scope = usePerformanceScope();
  const reduxPrimaryExam = useSelector(examsSelectors.selectPrimaryExam);
  return scope?.primaryExam ?? reduxPrimaryExam;
};

/**
 * True when the scoped primary exam is a single-exam experience: NCLEX-RN (a
 * nursing student never studies another exam) or a partner course (a course
 * student's numbers cover only their course exam, never Step/Shelf content).
 * There is no "All exams" scope then: widgets always use exam scope and hide
 * the exam/global toggle.
 */
export const useIsSingleExamScope = (): boolean => {
  const primaryExam = useScopedPrimaryExam();
  const isCourseStudent = useIsCourseStudent();
  return isNclexExam(primaryExam) || isCourseStudent;
};

/** Scope-aware drop-in for `usePrimaryExamType()`. */
export const useScopedPrimaryExamType = () => {
  const primaryExam = useScopedPrimaryExam();

  const derivedValues = useMemo(() => {
    const isNbme = isNbmeExam(primaryExam);
    const isComlex = isComlexExam(primaryExam);
    const isNclex = isNclexExam(primaryExam);
    const isPreclinical = isPreclinicalExam(primaryExam);
    const isCustom = isCustomExam(primaryExam) || isPreclinical;
    return { isNbme, isCustom, isComlex, isNclex, isPreclinical };
  }, [primaryExam?.id, primaryExam?.type]);

  return { ...derivedValues, primaryExam };
};

/** Scope-aware stats/flashcard fetchers; default to the current-user services. */
export const useScopedStatsFetchers = () => {
  const scope = usePerformanceScope();
  return useMemo(
    () => ({
      fetchVanity: scope?.fetchVanity ?? fetchVanityStats,
      fetchHistory: scope?.fetchHistory ?? fetchHistoryStats,
      fetchQbankPerformance:
        scope?.fetchQbankPerformance ?? fetchQbankPerformanceStats,
      fetchFlashcardProgress:
        scope?.fetchFlashcardProgress ?? fetchFlashcardProgressStats,
      fetchProgress: scope?.fetchProgress ?? fetchProgressStats,
      // Readiness is the student's own data (ORA-1026): a scoped host loads
      // its own tree, and gets readiness only through its own
      // `fetchTopicTreeWithReadiness`, whose backend route decides (the
      // faculty student detail, ORA-1105).
      fetchTopicTreeWithReadiness: scope
        ? (scope.fetchTopicTreeWithReadiness ??
          (async (examId?: string, view?: TopicTreeView) => {
            const tree = await scope.fetchTopicTree(examId, view);
            return tree ? { tree, readinessEnabled: false } : null;
          }))
        : fetchTopicTreeStatsWithReadiness,
      fetchTopicPerformance:
        scope?.fetchTopicPerformance ?? fetchTopicPerformanceStats,
      fetchFlashcardCounts: scope?.fetchFlashcardCounts ?? fetchFlashcardCounts,
      fetchWorkloadFlashcardCounts:
        scope?.fetchWorkloadFlashcardCounts ?? fetchWorkloadFlashcardCounts,
    }),
    [scope]
  );
};

const safeLocalGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore persistence failures */
  }
};

/**
 * Scope-aware toggle store. Defaults to `localStorage` (today's behavior); under
 * a provider it routes to the host's storage so the user's prefs are untouched.
 */
export const useScopedToggleStore = () => {
  const scope = usePerformanceScope();
  return useMemo(
    () => ({
      get: (key: string) => (scope ? scope.getToggle(key) : safeLocalGet(key)),
      set: (key: string, value: string) =>
        scope ? scope.setToggle(key, value) : safeLocalSet(key, value),
    }),
    [scope]
  );
};

export const useIsReadOnlyPerformance = () =>
  usePerformanceScope()?.readOnly ?? false;
